"use client";

import { useEffect, useMemo, useState } from "react";

import { useRovingFocus } from "@/lib/use-roving-focus";

export type TocItem = { id: string; label: string; depth?: number };

export type TocExtendDetail = {
  groupId: string;
  anchorId: string;
  items: TocItem[];
};

export function GeneralPageToc({ items }: { items: TocItem[] }) {
  const [activeId, setActiveId] = useState<string | null>(items[0]?.id ?? null);
  const [extraGroups, setExtraGroups] = useState<TocExtendDetail[]>([]);

  const merged = useMemo<TocItem[]>(() => {
    const out: TocItem[] = [];
    for (const item of items) {
      out.push(item);
      const group = extraGroups.find((g) => g.anchorId === item.id);
      if (group) {
        for (const sub of group.items) {
          out.push({ ...sub, depth: 1 });
        }
      }
    }
    // Defensive: append groups with no matching anchor.
    for (const group of extraGroups) {
      if (!items.some((i) => i.id === group.anchorId)) {
        for (const sub of group.items) out.push({ ...sub, depth: 1 });
      }
    }
    return out;
  }, [items, extraGroups]);

  const activeIndex = Math.max(
    0,
    merged.findIndex((i) => i.id === activeId),
  );
  const { ref, rovingProps } = useRovingFocus<HTMLElement>({
    homeIndex: activeIndex,
  });

  useEffect(() => {
    const onExtend = (event: Event) => {
      const detail = (event as CustomEvent<TocExtendDetail>).detail;
      if (!detail?.groupId || !Array.isArray(detail.items)) return;
      setExtraGroups((prev) => [...prev.filter((g) => g.groupId !== detail.groupId), detail]);
    };
    window.addEventListener("tf:toc-extend", onExtend);
    return () => window.removeEventListener("tf:toc-extend", onExtend);
  }, []);

  useEffect(() => {
    const nav = ref.current;
    const grid = nav?.closest(".grid");
    const content = grid && Array.from(grid.children).find((c) => !c.contains(nav));
    if (!nav || !(content instanceof HTMLElement)) return;
    const tabbable =
      'a[href]:not([tabindex="-1"]),button:not([disabled]):not([tabindex="-1"]),input:not([disabled]):not([tabindex="-1"]),select:not([disabled]):not([tabindex="-1"]),textarea:not([disabled]):not([tabindex="-1"]),[tabindex]:not([tabindex="-1"])';
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !e.shiftKey || !nav.offsetParent) return;
      const first = Array.from(content.querySelectorAll<HTMLElement>(tabbable)).find(
        (el) => el.offsetParent !== null,
      );
      if (first && first === document.activeElement) {
        e.preventDefault();
        (
          nav.querySelector<HTMLElement>('[aria-current="true"]') ??
          nav.querySelector<HTMLElement>('[data-roving][tabindex="0"]') ??
          nav.querySelector<HTMLElement>("[data-roving]")
        )?.focus();
      }
    };
    content.addEventListener("keydown", onKeyDown);
    return () => content.removeEventListener("keydown", onKeyDown);
  }, [ref]);

  useEffect(() => {
    if (merged.length === 0) return;
    const targets = merged
      .map((i) => document.getElementById(i.id))
      .filter((el): el is HTMLElement => el !== null);
    if (targets.length === 0) return;

    // Trigger when the heading crosses ~25% from the top.
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target.id) setActiveId(visible[0].target.id);
      },
      {
        rootMargin: "-25% 0px -65% 0px",
        threshold: [0, 1],
      },
    );
    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [merged]);

  if (merged.length === 0) return null;

  return (
    <nav ref={ref} aria-label="On this page" className="flex flex-col gap-2" {...rovingProps}>
      <p className="text-teal-07 mb-2 text-xs font-bold tracking-wider uppercase">On this page</p>
      {merged.map((item) => {
        const isActive = activeId === item.id;
        const indent = item.depth === 1 ? "ml-3" : "";
        return (
          <a
            key={item.id}
            href={`#${item.id}`}
            data-roving
            aria-current={isActive ? "true" : undefined}
            className={`${indent} border-l-2 pl-3 text-sm leading-snug transition-colors ${
              isActive
                ? "border-teal-07 text-teal-07 font-semibold"
                : "hover:border-teal-04 hover:text-teal-07 border-transparent text-black/60"
            }`}
          >
            {item.label}
          </a>
        );
      })}
    </nav>
  );
}
